import React, { useEffect, useState } from 'react'

const PersonalDetailsSection = ({
  profileData,
  setProfileData,
  userInfo,
  setUserInfo,
  showTitle = false,
  dependant = false,
  onValidityChange,
  setUnder16,
  readOnly = false
}) => {
  const [underage, setUnderAge] = useState(false)
  const dobDay = profileData?.date_of_birth?.day
  const dobMonth = profileData?.date_of_birth?.month
  const dobYear = profileData?.date_of_birth?.year
  const formattedDob =
    dobDay && dobMonth && dobYear
      ? `${String(dobDay).padStart(2, '0')}/${String(dobMonth).padStart(2, '0')}/${String(dobYear).padStart(4, '0')}`
      : ''
  const fullName = `${profileData?.first_name || ''} ${profileData?.last_name || ''}`.trim()

  // Validate DOB whenever it changes
  useEffect(() => {
    if (!dependant || !onValidityChange) return;

    const { day, month, year } = profileData?.date_of_birth || {}

    if (!day || !month || !year) {
      setUnder16(false)
      setUnderAge(false)
      onValidityChange(true) // invalid if any field missing
      return
    }

    const d = parseInt(day, 10)
    const m = parseInt(month, 10)
    const y = parseInt(year, 10)

    if (isNaN(d) || isNaN(m) || isNaN(y)) {
      setUnder16(false)
      setUnderAge(false)
      onValidityChange(true)
      return
    }

    const birthDate = new Date(y, m - 1, d)
    if (
      birthDate.getFullYear() !== y ||
      birthDate.getMonth() !== m - 1 ||
      birthDate.getDate() !== d
    ) {
      setUnder16(false)
      onValidityChange(false)
      return
    }

    const today = new Date()
    let age = today.getFullYear() - y
    if (today.getMonth() < m - 1 || (today.getMonth() === m - 1 && today.getDate() < d)) {
      age--
    }
    const validCheck = age < 16 // under 16 => valid
    if (validCheck) {
      setUnder16(true)
      setUnderAge(true)
      onValidityChange(true)
    } else {
      setUnder16(false)
      setUnderAge(false)
      onValidityChange(false)
    }


  }, [
    dependant,
    profileData?.date_of_birth?.day,
    profileData?.date_of_birth?.month,
    profileData?.date_of_birth?.year,
    onValidityChange
  ])

  return (
    <>
      {showTitle && (
        <div className="col-span-6">
          <div className="sm:col-span-6 border-b-2 border-b-[#DDCCDF]">
            <h2 className="text-2xl font-semibold">Profile</h2>
          </div>

          <div className="mt-4">
            <h3 className="text-lg font-medium">Personal Information</h3>
          </div>
        </div>
      )}

      {readOnly ? (
        <>
          {/* Full Name - Read Only */}
          <div className="col-span-6">
            <div className="flex items-center gap-3">
              <label className="text-sm/6 font-semibold min-w-[140px]">
                Name
              </label>
              <p className="text-base text-gray-900 sm:text-sm/6">
                {fullName}
              </p>
            </div>
          </div>

          {/* Date of Birth - Read Only */}
          <div className="col-span-6">
            <div className="flex items-center gap-3">
              <label htmlFor="dob" className="text-sm/6 font-semibold min-w-[140px]">
                Date Of Birth
              </label>
              <p className="text-base text-gray-900 sm:text-sm/6">
                {formattedDob}
              </p>
            </div>
          </div>

          {/* Sex - Read Only */}
          <div className="col-span-6">
            <div className="flex items-center gap-3">
              <label htmlFor="sex" className="text-sm/6 font-semibold min-w-[140px]">
                Sex Assigned At Birth
              </label>
              <p className="text-base text-gray-900 sm:text-sm/6">
                {userInfo?.gender || ''}
              </p>
            </div>
            <p className="text-[14px] text-slate-500 mt-1">
              This refers to your sex at birth. Many problems relate specifically to
              genetically male and female organs.
            </p>
          </div>
        </>
      ) : (
        <>
          {/* Full Name - Editable */}
          <div className="col-span-3">
            <label htmlFor="firstName" className="block text-sm/6 font-medium text-gray-900">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              value={profileData?.first_name || ''}
              onChange={(e) =>
                setProfileData({ ...profileData, first_name: e.target.value })
              }
              className="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-hub-primary sm:text-sm/6"
            />
          </div>

          <div className="col-span-3">
            <label htmlFor="lastName" className="block text-sm/6 font-medium text-gray-900">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              value={profileData?.last_name || ''}
              onChange={(e) =>
                setProfileData({ ...profileData, last_name: e.target.value })
              }
              className="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-hub-primary sm:text-sm/6"
            />
          </div>

          {/* Date of Birth - Editable */}
          <div className="col-span-2">
            <label htmlFor="dob-day" className="block text-sm/6 font-medium text-gray-900">
              Day
            </label>
            <input
              type="number"
              id="dob-day"
              min="1"
              max="31"
              value={profileData?.date_of_birth?.day || ''}
              onChange={(e) =>
                setProfileData({
                  ...profileData,
                  date_of_birth: {
                    ...profileData?.date_of_birth,
                    day: e.target.value,
                  },
                })
              }
              className="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-hub-primary sm:text-sm/6"
            />
          </div>

          <div className="col-span-2">
            <label htmlFor="dob-month" className="block text-sm/6 font-medium text-gray-900">
              Month
            </label>
            <input
              type="number"
              id="dob-month"
              min="1"
              max="12"
              value={profileData?.date_of_birth?.month || ''}
              onChange={(e) =>
                setProfileData({
                  ...profileData,
                  date_of_birth: {
                    ...profileData?.date_of_birth,
                    month: e.target.value,
                  },
                })
              }
              className="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-hub-primary sm:text-sm/6"
            />
          </div>

          <div className="col-span-2">
            <label htmlFor="dob-year" className="block text-sm/6 font-medium text-gray-900">
              Year
            </label>
            <input
              type="number"
              id="dob-year"
              value={profileData?.date_of_birth?.year || ''}
              onChange={(e) =>
                setProfileData({
                  ...profileData,
                  date_of_birth: {
                    ...profileData?.date_of_birth,
                    year: e.target.value,
                  },
                })
              }
              className="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-hub-primary sm:text-sm/6"
            />
          </div>

          {/* Sex - Editable */}
          {(!dependant || underage) && (
            <div className="col-span-6">
              <label htmlFor="sex" className="block text-sm/6 font-medium text-gray-900">
                Sex Assigned At Birth
              </label>
              <select
                id="sex"
                value={userInfo?.gender || ''}
                onChange={(e) =>
                  setUserInfo({ ...userInfo, gender: e.target.value })
                }
                className="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-hub-primary sm:text-sm/6"
              >
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
              <p className="text-[14px] text-slate-500 mt-1">
                This refers to your sex at birth. Many problems relate specifically to
                genetically male and female organs.
              </p>
            </div>
          )}

          {dependant && !underage && (
            <div className="col-span-6">
              <p className="text-red-600 text-sm mt-2">
                Dependants must be under 16. Dependants over 16 must create their
                own account.
              </p>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default PersonalDetailsSection
